import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { DayStatus, DuplicateDayEntry } from '@core/components/times/times.types';
import { clearOverlapErrors, markOverlaps, toMinutes, setErr, delErr } from './times-helpers';

export const fromBeforeToValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!(control instanceof FormGroup)) return null;

  const fromCtrl: AbstractControl<string | null> | null
    = control.get('from') as AbstractControl<string | null> | null;
  const toCtrl: AbstractControl<string | null> | null
    = control.get('to') as AbstractControl<string | null> | null;
  const from: string = fromCtrl?.value ?? '';
  const to: string = toCtrl?.value ?? '';

  if (!from || !to) {
    if (fromCtrl) delErr(fromCtrl, 'rangeOrder');

    if (toCtrl) delErr(toCtrl, 'rangeOrder');

    return null;
  }

  const isInvalid: boolean = from >= to;

  if (isInvalid) {
    if (fromCtrl) setErr(fromCtrl, 'rangeOrder');

    if (toCtrl) setErr(toCtrl, 'rangeOrder');

    return { rangeOrder: true };
  }

  if (fromCtrl) delErr(fromCtrl, 'rangeOrder');

  if (toCtrl) delErr(toCtrl, 'rangeOrder');

  return null;
};
export const uniqueDaysAcrossGroups: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const groupsFa: FormArray<FormGroup> | null = control.get('groups') as FormArray<FormGroup> | null;

  if (!groupsFa) return null;

  const groups: FormGroup[] = groupsFa.controls;
  const seen: Map<number, number> = new Map<number, number>();
  const duplicates: Array<{ day: number; groups: number[]; }> = [];

  groups.forEach((group: FormGroup, groupIndex: number) => {
    const days: DayStatus[] = (group.get('days')?.value ?? []) as DayStatus[];

    days.forEach((dayStatus: DayStatus, dayIndex: number) => {
      if (dayStatus === 'neutral') return;

      const prev: number | undefined = seen.get(dayIndex);

      if (prev === undefined)
        seen.set(dayIndex, groupIndex);
      else {
        const entry: DuplicateDayEntry | undefined = duplicates.find((x: DuplicateDayEntry): boolean => x.day === dayIndex);

        if (entry) {
          if (!entry.groups.includes(groupIndex)) entry.groups.push(groupIndex);
        } else
          duplicates.push({ day: dayIndex, groups: [ prev, groupIndex ] });
      }
    });
  });

  return duplicates.length ? { duplicateDays: duplicates } : null;
};
export const groupConsistencyValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const days: DayStatus[] = (control.get('days')?.value ?? []) as DayStatus[];
  const isAllDayOpen: boolean = Boolean(control.get('allDayOpen')?.value);
  const timeRangeArray: FormArray<FormGroup> = control.get('ranges') as FormArray<FormGroup>;
  const pickedDays: Set<DayStatus> = new Set<DayStatus>(days.filter((d) => d !== 'neutral'));
  const isAnyClosed: boolean = pickedDays.has('closed');
  const isAnyOpen: boolean = pickedDays.has('open');

  if (isAllDayOpen) {
    clearOverlapErrors(timeRangeArray);

    if (isAnyClosed) return { allDayOpenWithClosedDays: true };

    if (timeRangeArray.enabled) return { allDayOpenButRangesEnabled: true };

    return null;
  }

  if (isAnyClosed) {
    clearOverlapErrors(timeRangeArray);

    if (isAnyOpen) return { mixedOpenClosed: true };

    if (timeRangeArray.length > 0 && timeRangeArray.enabled) return { closedWithRanges: true };

    return null;
  }

  if (timeRangeArray.length === 0) return { openWithoutRanges: true };

  const hasInvalidOrder: boolean = timeRangeArray.controls.some((c: FormGroup) => {
    const fromMinutes: number | null = toMinutes(c.get('from')?.value as string);
    const toMinutesVal: number | null = toMinutes(c.get('to')?.value as string);

    return fromMinutes === null || toMinutesVal === null || fromMinutes >= toMinutesVal;
  });

  if (hasInvalidOrder) return { invalidRangeOrder: true };

  const hasOverlaps: boolean = markOverlaps(timeRangeArray);

  if (hasOverlaps) return { overlappingRanges: true };

  return null;
};
